import { Injectable } from '@angular/core';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { PaymentData } from '@payments/pages/payment-confirmation/services/payment-confirmation.service';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { ShareViaEmailModalComponent } from '@shared/components/share-via-email-modal/share-via-email-modal.component';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';
import {
  ShareEventActions,
  SharePaymentConfirmationViaEmailModel,
} from '@usf/file-share-types';
import { SharePaymentsEmailStoreService } from '@usf/ngrx-payment';
import { take } from 'rxjs';
import { FileHandlerPaymentsService } from './file-handler-payments.service';
import { AlertAnalyticsService } from '@usf/ngrx-alerts';

@Injectable({
  providedIn: 'root',
})
export class PaymentsShareViaEmailService {
  constructor(
    public readonly loadingSpinnerService: LoadingSpinnerService,
    protected readonly panAppState: PanAppState,
    private readonly modalController: ModalController,
    private readonly modalService: ModalService,
    public readonly platform: PlatformService,
    private readonly fileHandlerPaymentsService: FileHandlerPaymentsService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly shareEmailService: SharePaymentsEmailStoreService,
    private readonly alertAnalyticsService: AlertAnalyticsService,
  ) {}

  async showShareViaEmailModal(paymentData: PaymentData) {
    const documentName = this.fileHandlerPaymentsService.composeFileName(
      'Payment Confirmation',
    );
    this.modalService.setModalOptions(
      this.platform?.platformType === this.platform?.platformEnum?.mobile,
      ModalWithNavComponent,
      {
        rootPage: ShareViaEmailModalComponent,
        rootPageParams: {
          documentName,
          shareAction: ShareEventActions.SharePaymentConfirmation,
          isMobile:
            this.platform?.platformType === this.platform?.platformEnum?.mobile,
        },
      },
      this.platform?.platformType === this.platform?.platformEnum?.mobile
        ? 'share-via-email-modal-mobile'
        : 'share-via-email-modal-desktop',
      true,
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();

    this.alertAnalyticsService.trackSharePaymentConfirmationModalOpen();

    await modal.onDidDismiss().then((resp: any) => {
      if (
        !!resp?.data &&
        !!resp?.data?.emailData &&
        !!resp?.data?.emailString &&
        resp?.data?.emailData?.emailTo !== undefined &&
        resp?.data?.emailData?.message !== undefined &&
        resp?.data?.emailData?.sendCopy !== undefined
      ) {
        const emailData = {
          ...resp?.data?.emailData,
          emailString: resp?.data?.emailString,
        };
        this.shareViaEmailPaymentConfirmation(
          documentName,
          paymentData,
          emailData,
        );
      }
    });
  }

  async shareViaEmailPaymentConfirmation(
    documentName: string,
    paymentData: PaymentData,
    emailData: any,
  ) {
    try {
      this.loadingSpinnerService.createSpinnerModal().then(async () => {
        this.panAppState.user$.pipe(take(1)).subscribe(async user => {
          const payload = {
            customerReview: paymentData.customers,
            paymentSummary: paymentData.paymentSummary,
          };

          const sharePaymentConfirmationViaEmail: SharePaymentConfirmationViaEmailModel =
            {
              fileName: `${documentName}.pdf`,
              ccAddresses: emailData.emailTo?.split(','),
              firstName: user?.firstName,
              lastName: user?.lastName,
              message: emailData.message,
              sendMeACopy: emailData.sendCopy,
              sharedBy: user?.email,
              shareAction: ShareEventActions.SharePaymentConfirmation,
              payload,
            };
          this.dispatchShareViaEmailActions(
            emailData,
            sharePaymentConfirmationViaEmail,
          );
        });
      });
    } catch (error) {
      this.loadingSpinnerService.dismissSpinnerModal();
      console.error('Error sharing review order via email:', error);
    }
  }

  dispatchShareViaEmailActions(
    emailData: any,
    sharePaymentConfirmationViaEmail: SharePaymentConfirmationViaEmailModel,
  ) {
    // alerts analytics
    this.alertAnalyticsService.trackShareFilePaymentConfirmation();

    this.shareEmailService.sharePaymentConfirmation(
      sharePaymentConfirmationViaEmail,
    );

    const handleCloseModal = (isSuccessful: boolean) => {
      this.loadingSpinnerService.dismissSpinnerModal();
      isSuccessful
        ? this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.sharePaymentConfirmation.successMessage',
            ) + emailData.emailString,
            'green-toast',
            MessageTypeEnum.success,
            [],
          )
        : this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.sharePaymentConfirmation.failMessage',
            ) + emailData.emailString,
            'red-toast',
            MessageTypeEnum.error,
            [],
          );
    };
    this.shareEmailService.sharePaymentConfirmationSuccess$().subscribe(() => {
      handleCloseModal(true);
    });

    this.shareEmailService.sharePaymentConfirmationFail$().subscribe(() => {
      handleCloseModal(false);
    });
  }
}
